import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { generateImage } from "../types";

interface ImageGeneratorProps {
  initialImageUrl?: string;
  itemName: string;
  itemType: "plant" | "object";
  itemId?: string;
  onImageGenerated: (imageUrl: string) => void;
}

const ImageGenerator: React.FC<ImageGeneratorProps> = ({
  initialImageUrl,
  itemName,
  itemType,
  itemId,
  onImageGenerated,
}) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | undefined>(initialImageUrl);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePrompt = (): string => {
    if (itemType === "plant") {
      return `A photorealistic image of a ${itemName} plant, outdoor garden setting, natural lighting, detailed`;
    } else {
      return `A photorealistic image of a ${itemName} garden object, outdoor setting, natural lighting, detailed`;
    }
  };

  const handleGenerateImage = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const response = await generateImage(generatePrompt(), itemId, itemType);

      if (response.success && response.data) {
        setImageUrl(response.data);
        onImageGenerated(response.data);
      } else {
        setError(t('admin.errorMessages.failedToGenerateImage'));
      }
    } catch (err) {
      setError(t('admin.errorMessages.imageGenerationError'));
      console.error("Image generation error:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="image-generator">
      <div className="form-group">
        <label>{t('admin.imagePreview')}</label>
        <div className="image-preview-container">
          {imageUrl ? (
            <div className="image-preview">
              <img
                src={imageUrl}
                alt={`${itemName} preview`}
                className="preview-image"
              />
            </div>
          ) : (
            <div className="no-image-placeholder">
              <span>{t('admin.noImage')}</span>
            </div>
          )}
        </div>

        <div className="mt-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleGenerateImage}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t('admin.generatingImage')}
              </>
            ) : imageUrl ? (
              t('admin.regenerateImage')
            ) : (
              t('admin.generateImage')
            )}
          </button>
          {error && <div className="text-danger mt-2">{error}</div>}
          <div className="text-muted mt-2 small">
            <p>
              {t('admin.imageStored')}{itemType}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageGenerator;
